import { Injectable } from '@angular/core';
import { UnleashClient } from 'unleash-proxy-client';
import { ENVIRONMENT } from 'src/environments/environment';


import { FeatureFlagPort } from 'src/app/domain/features-flags/port/feature-flag.port';
import { FeatureFlagEnum } from 'src/app/domain/features-flags/enum/featureFlag.enum';
import { Observable, of } from 'rxjs';
import { ValueLocalStorage } from '../../interfaces/feature-flag.interfaces';
import { Auth } from 'src/app/domain/models/auth.model';


@Injectable({
  providedIn: 'root'
})

export class UnleashAdapter  implements FeatureFlagPort {
  private unleash!: UnleashClient
  public idCommerce!: string;

  constructor() {}

  /**
   * Initializes the Unleash client with the configuration specified in the environment variables.
   * The client is configured with the URL, client key, application name, and environment.
   * It also sets a refresh interval and context properties.
   *
   * @private
   * @method
   * @name initializeUnleashClient
   */
  private initializeUnleashClient(authData: Auth | undefined): Observable<void> {
    const hasBranch = !(authData?.idBranch === "0" || authData?.idBranch === "");

    const propertiesObject: {
      commerceIds: string;
      hasBranches: string;
    } = {
      commerceIds: authData?.idCommerce?.toString() || '',
      hasBranches: hasBranch.toString(),
    }

    this.unleash = new UnleashClient({
      url: ENVIRONMENT.unleash.url,
      clientKey: ENVIRONMENT.unleash.clientKey,
      appName: ENVIRONMENT.unleash.appName,
      environment: ENVIRONMENT.unleash.environment,
      refreshInterval: 0,
      context:{
        properties: {...propertiesObject}
      }
    });
    return new Observable<void>((observer) => {
      this.unleash.start().then(() => {
        observer.next();
        observer.complete();
      }).catch((error) => {
        observer.error(error);
      });
    });
  }

  /**
   * Configures the feature flag by initializing the Unleash client.
   *
   * This method sets up the necessary configuration for feature flags
   * by calling the `initializeUnleashClient` method.
   *
   * @returns {void}
   */
  configFeatureFlag(authData: Auth | undefined): Observable<void> {
    if(!authData) this.idCommerce = '';
    else this.idCommerce = authData.idCommerce.toString() || '';
    return this.initializeUnleashClient(authData);
  }

    /**
   * Retrieves the status of a feature flag from local storage.
   *
   * @param {FeatureFlagEnum} id - The identifier of the feature flag to retrieve.
   * @returns {Observable<boolean>} An observable that emits `true` if the feature flag is enabled, `false` otherwise.
   *
   * @remarks
   * This method attempts to fetch the feature flag status from the local storage key "unleash:repository:repo".
   * If the key does not exist or if there is an error parsing the stored values, the method returns `false`.
   */
  getFeatureFlag(id: FeatureFlagEnum): Observable<boolean> {
    const values = localStorage.getItem("unleash:repository:repo");
    if (!values) {
      return of(false);
    }
    try {
      const parsedValues: ValueLocalStorage[] = JSON.parse(values);
      const flag = parsedValues.find((value) => value.name === id);
      return of(flag?.enabled ?? false);
    } catch (error) {
      return of(false);
    }
  }
}
